import React, { useState, useEffect } from 'react';
import { FileText, Copy, Download, Trash2, Eye, Edit3, Calendar, User } from 'lucide-react';
import { lerToken } from '../services/sessao';

interface GeneratedDoc {
  id: string;
  title: string;
  content: string;
  purpose?: string;
  interlocutor_name?: string;
  interlocutor_email?: string;
  createdAt: string;
  updatedAt: string;
}

export default function SaasMeusDocumentos() {
  const [docs, setDocs] = useState<GeneratedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<GeneratedDoc | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editPurpose, setEditPurpose] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDocs();
  }, []);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch('/api/ia/generated-docs', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setDocs(data.documents || []);
    } catch (err) {
      console.error('Erro ao carregar documentos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('✅ Copiado!');
    } catch {
      alert('Erro ao copiar.');
    }
  };

  const handleDownload = (doc: GeneratedDoc) => {
    const blob = new Blob([`# ${doc.title}\n\n`, `Criado em: ${new Date(doc.createdAt).toLocaleString('pt-BR')}\n`, doc.purpose ? `Finalidade: ${doc.purpose}\n` : '', doc.interlocutor_name ? `Interlocutor: ${doc.interlocutor_name}\n` : '', '\n---\n\n', doc.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = doc.title.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60);
    a.download = `${safe}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (doc: GeneratedDoc) => {
    if (!window.confirm(`Excluir o documento "${doc.title}"?`)) return;
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch(`/api/ia/generated-docs/${doc.id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSelectedDoc(null);
      await loadDocs();
    } catch (err) {
      alert('Erro ao excluir.');
    }
  };

  const handleEdit = (doc: GeneratedDoc) => {
    setEditTitle(doc.title);
    setEditContent(doc.content);
    setEditPurpose(doc.purpose || '');
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedDoc) return;
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch(`/api/ia/generated-docs/${selectedDoc.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: editTitle,
          content: editContent,
          purpose: editPurpose,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEditMode(false);
      await loadDocs();
      setSelectedDoc({ ...selectedDoc, title: editTitle, content: editContent, purpose: editPurpose });
    } catch (err) {
      alert('Erro ao salvar.');
    }
  };

  const filtered = docs.filter(d =>
    d.title.toLowerCase().includes(search.toLowerCase()) ||
    (d.purpose || '').toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ background: 'linear-gradient(135deg, #fff 0%, #fff5f8 100%)', borderRadius: 16, padding: '24px 32px', marginBottom: 24, border: '1px solid #fecede', display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ width: 60, height: 60, background: 'rgba(255,64,129,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4081' }}>
          <FileText size={32} />
        </div>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>Meus Documentos Gerados</h2>
          <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 14 }}>Documentos criados pela LIZ — pode visualizar, editar, copiar, baixar ou excluir</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedDoc ? '380px 1fr' : '1fr', gap: 20 }}>
        {/* Lista */}
        <div>
          <input
            type="text"
            placeholder="🔍 Buscar documentos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8, marginBottom: 12, fontSize: 14 }}
          />
          {loading ? (
            <p style={{ color: '#888' }}>Carregando...</p>
          ) : filtered.length === 0 ? (
            <div style={{ background: '#f8f8f8', borderRadius: 12, padding: 32, textAlign: 'center', color: '#888' }}>
              <FileText size={40} style={{ opacity: 0.3 }} />
              <p style={{ margin: '12px 0 0 0' }}>{docs.length === 0 ? 'Nenhum documento ainda. Converse com a LIZ e clique em "Salvar Documento" em uma resposta.' : 'Nenhum documento encontrado para essa busca.'}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(doc => (
                <div
                  key={doc.id}
                  onClick={() => { setSelectedDoc(doc); setEditMode(false); }}
                  style={{
                    background: selectedDoc?.id === doc.id ? '#fff5f8' : '#fff',
                    border: `1px solid ${selectedDoc?.id === doc.id ? '#ff4081' : '#eee'}`,
                    borderRadius: 8, padding: 12, cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#333', marginBottom: 4 }}>{doc.title}</div>
                  <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span><Calendar size={11} style={{ verticalAlign: 'middle' }} /> {new Date(doc.createdAt).toLocaleDateString('pt-BR')}</span>
                    {doc.interlocutor_name && <span><User size={11} style={{ verticalAlign: 'middle' }} /> {doc.interlocutor_name}</span>}
                  </div>
                  {doc.purpose && <div style={{ fontSize: 12, color: '#999', marginTop: 4, fontStyle: 'italic' }}>{doc.purpose.slice(0, 80)}{doc.purpose.length > 80 ? '...' : ''}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detalhes */}
        {selectedDoc && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #eee', minHeight: 400 }}>
            {editMode ? (
              <>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{ width: '100%', fontSize: 20, fontWeight: 700, padding: 8, border: '1px solid #ddd', borderRadius: 6, marginBottom: 12 }}
                />
                <input
                  type="text"
                  value={editPurpose}
                  onChange={(e) => setEditPurpose(e.target.value)}
                  placeholder="Finalidade"
                  style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, marginBottom: 12, fontSize: 13 }}
                />
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={20}
                  style={{ width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={handleSaveEdit} style={{ padding: '8px 16px', background: '#ff4081', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Salvar Alterações</button>
                  <button onClick={() => setEditMode(false)} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ margin: '0 0 8px 0', color: '#333' }}>{selectedDoc.title}</h2>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span>📅 {new Date(selectedDoc.createdAt).toLocaleString('pt-BR')}</span>
                  {selectedDoc.interlocutor_name && <span>👤 {selectedDoc.interlocutor_name}</span>}
                </div>
                {selectedDoc.purpose && (
                  <div style={{ background: '#fff5f8', border: '1px solid #fecede', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                    <strong>Finalidade:</strong> {selectedDoc.purpose}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  <button onClick={() => handleCopy(selectedDoc.content)} style={{ padding: '6px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <Copy size={14} /> Copiar
                  </button>
                  <button onClick={() => handleDownload(selectedDoc)} style={{ padding: '6px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <Download size={14} /> Baixar .txt
                  </button>
                  <button onClick={() => handleEdit(selectedDoc)} style={{ padding: '6px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <Edit3 size={14} /> Editar
                  </button>
                  <button onClick={() => handleDelete(selectedDoc)} style={{ padding: '6px 12px', background: '#ffe5e5', border: '1px solid #ffaaaa', color: '#cc0000', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <Trash2 size={14} /> Excluir
                  </button>
                </div>
                <div style={{ background: '#fafafa', borderRadius: 8, padding: 16, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6, color: '#333', maxHeight: '60vh', overflowY: 'auto' }}>
                  {selectedDoc.content}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
